import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Which day cells carry event chips, and how many. Sparse and irregular —
 * a calendar with an event in every cell reads as a test fixture, not a month.
 */
const EVENTS_BY_CELL: Readonly<Record<number, number>> = {
    3: 1,
    5: 2,
    9: 1,
    12: 3,
    16: 1,
    18: 2,
    21: 1,
    24: 1,
    27: 2,
    30: 1,
};

interface CalendarGridSkeletonProps {
    /** Weeks to draw. Six covers the longest month layout. */
    readonly weeks?: number;
    readonly className?: string;
}

/**
 * Loading placeholder for a month calendar.
 *
 * Draws the real 7-column grid with a weekday header and sparsely-populated
 * event chips, so the shape is recognisably a calendar rather than a 560px grey
 * rectangle — which is what stood here before.
 */
export function CalendarGridSkeleton({ weeks = 5, className }: CalendarGridSkeletonProps) {
    const cells = weeks * 7;
    return (
        <div className={cn('overflow-hidden rounded-md border border-border-default', className)}>
            <div className="grid grid-cols-7 border-b border-border-default bg-bg-table-header">
                {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="flex h-9 items-center justify-center">
                        <Skeleton className="h-2 w-8 rounded-sm" />
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {Array.from({ length: cells }).map((_, i) => (
                    <div
                        key={i}
                        className="flex h-[104px] flex-col gap-1 border-r border-b border-border-default p-1.5 last:border-r-0 [&:nth-child(7n)]:border-r-0"
                    >
                        <SkeletonText size="xs" width="18px" />
                        {Array.from({ length: EVENTS_BY_CELL[i] ?? 0 }).map((_e, j) => (
                            <Skeleton key={j} className="h-4 w-full rounded-sm" />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}
